import React, { useEffect, useMemo, useState } from "react";
import FormItem from "@components/FormItem/FormItem";
import { DRAWER_TEXT_MAX_CHARS } from "@components/SideDrawer/SideDrawer.schema";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { DryBulkDecisionAttachment } from "@services/dry-bulk-nomination/types";
import { DRY_BULK_NOMINATION_QUERY_KEY, getDryBulkBerths } from "@services/dryBulkNominationApi";
import { useQuery } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { byLabel, codeLabel } from "../hooks/useDryBulkReferenceOptions";
import DecisionAttachmentsField from "./DecisionAttachmentsField";

const LANG_BASE = "vessel-nomination.dry-bulk.review";

type Values = { expectedBerthCode: string; berthName: string; text: string };

export interface DecisionSubmit {
  expectedBerthCode?: string;
  text: string;
  attachments: DryBulkDecisionAttachment[];
}

interface Props {
  mode: "approve" | "reject";
  nominationId: string;
  isOpen: boolean;
  isSubmitting?: boolean;
  fieldErrors?: Record<string, string>;
  preselectedBerthCode?: string | null;
  onClose: () => void;
  onSubmit: (values: DecisionSubmit) => void;
}

/** Approve (Expected Berth from the port's list, optional comments) and Reject (reason required) panels, VN-21 / VN-22. */
const DecisionDrawer: React.FC<Props> = ({
  mode,
  nominationId,
  isOpen,
  isSubmitting,
  fieldErrors,
  preselectedBerthCode,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isApprove = mode === "approve";
  const form = useForm<Values>({
    mode: "onTouched",
    defaultValues: { expectedBerthCode: "", berthName: "", text: "" },
  });
  const [attachments, setAttachments] = useState<DryBulkDecisionAttachment[]>([]);

  const berthsQuery = useQuery({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, "berths", nominationId],
    queryFn: () => getDryBulkBerths(nominationId),
    enabled: isOpen && isApprove,
  });
  const berths = useMemo(() => berthsQuery.data ?? [], [berthsQuery.data]);
  const berthCode = form.watch("expectedBerthCode");
  const berthOptions = useMemo(
    () =>
      berths
        .map((berth) => ({
          value: berth.berthCode,
          label: codeLabel(berth.berthCode, berth.berthName),
        }))
        .sort(byLabel),
    [berths],
  );
  const berthName = berths.find((berth) => berth.berthCode === berthCode)?.berthName ?? "";

  useEffect(() => {
    form.setValue("berthName", berthName);
  }, [berthName, form]);

  useEffect(() => {
    if (!isOpen) return;
    form.reset({ expectedBerthCode: "", berthName: "", text: "" });
    setAttachments([]);
  }, [form, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      isApprove &&
      preselectedBerthCode &&
      berths.some((berth) => berth.berthCode === preselectedBerthCode) &&
      !form.getValues("expectedBerthCode")
    ) {
      form.setValue("expectedBerthCode", preselectedBerthCode);
    }
  }, [berths, form, isApprove, isOpen, preselectedBerthCode]);

  useEffect(() => {
    const serverField = isApprove ? "expectedBerthCode" : "text";
    const message = isApprove ? fieldErrors?.expectedBerthCode : fieldErrors?.reason;
    if (message) form.setError(serverField, { type: "server", message });
  }, [fieldErrors, form, isApprove]);

  const required = t(`${LANG_BASE}.required`);
  const submit = form.handleSubmit((values) => {
    if (isApprove && !values.expectedBerthCode) {
      form.setError("expectedBerthCode", { type: "required", message: required });
      return;
    }
    if (!isApprove && !values.text.trim()) {
      form.setError("text", { type: "required", message: required });
      return;
    }
    onSubmit({
      expectedBerthCode: isApprove ? values.expectedBerthCode : undefined,
      text: values.text.trim(),
      attachments,
    });
  });

  const attachmentError = Object.entries(fieldErrors ?? {}).find(([path]) =>
    path.startsWith("attachments"),
  )?.[1];

  return (
    <div data-testid={`dry-bulk-${mode}-drawer`}>
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={submit}
          isSubmitting={isSubmitting}
          title={t(`${LANG_BASE}.${mode}-title`)}
          submitLabel={t(`${LANG_BASE}.${mode}-submit`)}
          cancelLabel={t("common.cancel")}
        >
          <div className="w-[400px] p-6 flex flex-col gap-2">
            {isApprove && (
              <>
                <FormItem<Values, "expectedBerthCode">
                  required
                  testId="dry-bulk-decision-berth"
                  name="expectedBerthCode"
                  type="autocomplete"
                  label={t(`${LANG_BASE}.berth`)}
                  options={berthOptions}
                />
                <FormItem<Values, "berthName">
                  disabled
                  testId="dry-bulk-decision-berth-name"
                  name="berthName"
                  type="text"
                  label={t(`${LANG_BASE}.berth-name`)}
                />
              </>
            )}
            <FormItem<Values, "text">
              required={!isApprove}
              testId={`dry-bulk-decision-${isApprove ? "comments" : "reason"}`}
              name="text"
              type="textarea"
              label={t(`${LANG_BASE}.${isApprove ? "comments" : "reason"}`)}
              maxChars={DRAWER_TEXT_MAX_CHARS}
              showCharCounter
              minRows={4}
            />
            <DecisionAttachmentsField
              testId="dry-bulk-decision-attachments"
              attachments={attachments}
              onChange={setAttachments}
              error={attachmentError}
            />
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default DecisionDrawer;
